import { useState, useRef, useCallback, ReactNode } from "react";
import { createPortal } from "react-dom";

interface TooltipProps {
  content: ReactNode;
  children: ReactNode;
  /** Position of tooltip relative to trigger */
  position?: "top" | "bottom" | "left" | "right";
  /** Delay before showing tooltip in ms */
  delay?: number;
}

function calculatePosition(
  triggerEl: HTMLElement,
  tooltipEl: HTMLElement,
  position: "top" | "bottom" | "left" | "right"
): { top: number; left: number } {
  const triggerRect = triggerEl.getBoundingClientRect();
  const tooltipRect = tooltipEl.getBoundingClientRect();
  const scrollY = window.scrollY;
  const scrollX = window.scrollX;

  let top = 0;
  let left = 0;

  switch (position) {
    case "top":
      top = triggerRect.top + scrollY - tooltipRect.height - 8;
      left = triggerRect.left + scrollX + triggerRect.width / 2 - tooltipRect.width / 2;
      break;
    case "bottom":
      top = triggerRect.bottom + scrollY + 8;
      left = triggerRect.left + scrollX + triggerRect.width / 2 - tooltipRect.width / 2;
      break;
    case "left":
      top = triggerRect.top + scrollY + triggerRect.height / 2 - tooltipRect.height / 2;
      left = triggerRect.left + scrollX - tooltipRect.width - 8;
      break;
    case "right":
      top = triggerRect.top + scrollY + triggerRect.height / 2 - tooltipRect.height / 2;
      left = triggerRect.right + scrollX + 8;
      break;
  }

  // Keep tooltip within viewport bounds
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const padding = 8;

  if (left < padding) left = padding;
  if (left + tooltipRect.width > viewportWidth - padding) {
    left = viewportWidth - tooltipRect.width - padding;
  }
  if (top < scrollY + padding) top = scrollY + padding;
  if (top + tooltipRect.height > scrollY + viewportHeight - padding) {
    top = scrollY + viewportHeight - tooltipRect.height - padding;
  }

  return { top, left };
}

/**
 * Custom styled tooltip component to replace native browser tooltips.
 */
export default function Tooltip({
  content,
  children,
  position = "top",
  delay = 300,
}: TooltipProps) {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLSpanElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showTooltip = () => {
    timeoutRef.current = setTimeout(() => {
      setVisible(true);
    }, delay);
  };

  const hideTooltip = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setVisible(false);
  };

  // Callback ref to position tooltip when it mounts
  const tooltipCallbackRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (node && triggerRef.current) {
        const newCoords = calculatePosition(triggerRef.current, node, position);
        // Only update if coords changed to avoid infinite loop
        setCoords((prev) => {
          if (prev.top !== newCoords.top || prev.left !== newCoords.left) {
            return newCoords;
          }
          return prev;
        });
      }
    },
    [position]
  );

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onFocus={showTooltip}
        onBlur={hideTooltip}
        className="inline-flex"
      >
        {children}
      </span>
      {visible &&
        createPortal(
          <div
            ref={tooltipCallbackRef}
            role="tooltip"
            style={{
              position: "absolute",
              top: coords.top,
              left: coords.left,
              zIndex: 9999,
              pointerEvents: "none",
            }}
            className="px-2.5 py-1.5 text-xs font-medium text-gray-700 dark:text-dark-text dark:bg-dark-bg-secondary bg-white border border-gray-200 dark:border-dark-border rounded-lg shadow-lg animate-fade-in"
          >
            {content}
          </div>,
          document.body
        )}
    </>
  );
}
